import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import CircularProgress from '@mui/material/CircularProgress';
import { Mic, Settings, AudioFile, Collections, Menu as MenuIcon, Home, MicNone, Folder, Assessment, Logout, Translate, GraphicEq } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

// Thème et contextes
import theme from './theme';
import { AppProvider } from './contexts/AppContext';
import { logout } from './services/auth';

// Composants de base
import ErrorBoundary from './components/ErrorBoundary';
import AdminRoute from './components/AdminRoute';
import PrivateRoute from './components/PrivateRoute';

// Lazy loading des pages pour améliorer les performances
const HomePage = lazy(() => import('./views/HomePage'));
const VoiceRecorder = lazy(() => import('./views/VoiceRecorder'));
const RecordingsPage = lazy(() => import('./views/RecordingsPage'));
const MyFilesPage = lazy(() => import('./views/MyFilesPage'));
const SettingsPage = lazy(() => import('./views/SettingsPage'));
const SharedCollectionPage = lazy(() => import('./views/SharedCollectionPage'));
const SharedRecordingPage = lazy(() => import('./views/SharedRecordingPage'));
const VoiceCollectionPageRefactored = lazy(() => import('./views/VoiceCollectionPageRefactored'));
const TranslationCollectionPage = lazy(() => import('./views/TranslationCollectionPage'));
const SharedTranslationPage = lazy(() => import('./views/SharedTranslationPage'));
const TranscriptionPage = lazy(() => import('./views/TranscriptionPage'));
const SharedTranscriptionPage = lazy(() => import('./views/SharedTranscriptionPage'));
const LoginPage = lazy(() => import('./views/LoginPage'));
const GoogleCallbackPage = lazy(() => import('./views/GoogleCallbackPage'));
const OAuthCallbackPage = lazy(() => import('./views/OAuthCallbackPage'));
const AdminPage = lazy(() => import('./views/AdminPage'));

// Composant de chargement
const LoadingFallback = () => (
  <Box sx={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '80vh',
    bgcolor: '#1a1a2e'
  }}>
    <CircularProgress sx={{ color: '#4caf50' }} size={60} />
  </Box>
);


// Composant de navigation avec React.memo pour éviter les re-renders inutiles
const Navigation = React.memo(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState(null);

  // Récupérer les infos utilisateur pour vérifier si admin
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const isAdmin = user?.is_admin === true;

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    handleMenuClose();
  };

  // Définir les items de navigation (filtrer Admin si pas admin)
  const allNavItems = [
    { path: '/voice-recorder', label: 'Enregistrer', icon: <Mic /> },
    { path: '/recordings', label: 'Mes fichiers', icon: <AudioFile /> },
    { path: '/collections', label: 'Collections', icon: <Collections /> },
    { path: '/translations', label: 'Traductions', icon: <Translate /> },
    { path: '/transcription', label: 'Transcription', icon: <GraphicEq /> },
    { path: '/admin', label: 'Admin', icon: <Assessment />, adminOnly: true },
    { path: '/settings', label: 'Paramètres', icon: <Settings /> }
  ];

  // Filtrer les items selon les permissions
  const navItems = allNavItems.filter(item => !item.adminOnly || isAdmin);

  return (
    <AppBar
      position="sticky"
      sx={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 4 }, py: { xs: 1.5, md: 2 } }}>
        {/* Logo/Titre avec animation */}
        <Box
          onClick={() => navigate('/')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, md: 2 },
            cursor: 'pointer',
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)'
            }
          }}
        >
          {/* Logo microphone animé */}
          <Box
            sx={{
              position: 'relative',
              width: { xs: 45, md: 55 },
              height: { xs: 45, md: 55 },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Cercle pulsant en arrière-plan */}
            <Box
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(76,175,80,0.3) 0%, rgba(76,175,80,0) 70%)',
                animation: 'ripple 2s ease-out infinite',
                '@keyframes ripple': {
                  '0%': {
                    transform: 'scale(0.8)',
                    opacity: 0.8
                  },
                  '50%': {
                    transform: 'scale(1.2)',
                    opacity: 0.4
                  },
                  '100%': {
                    transform: 'scale(0.8)',
                    opacity: 0.8
                  }
                }
              }}
            />

            {/* Container du micro avec effet glassmorphism */}
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                width: { xs: 42, md: 50 },
                height: { xs: 42, md: 50 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(145deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
                borderRadius: '14px',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.3)',
                border: '1px solid rgba(255,255,255,0.2)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: '-2px',
                  borderRadius: '16px',
                  padding: '2px',
                  background: 'linear-gradient(135deg, rgba(76,175,80,0.6), rgba(33,150,243,0.6), rgba(156,39,176,0.6))',
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                  animation: 'borderRotate 3s linear infinite',
                },
                '@keyframes borderRotate': {
                  '0%': {
                    filter: 'hue-rotate(0deg)'
                  },
                  '100%': {
                    filter: 'hue-rotate(360deg)'
                  }
                }
              }}
            >
              {/* Icône microphone avec animation */}
              <Mic
                sx={{
                  fontSize: { xs: 26, md: 32 },
                  color: '#fff',
                  filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))',
                  animation: 'micPulse 2s ease-in-out infinite',
                  '@keyframes micPulse': {
                    '0%, 100%': {
                      transform: 'translateY(0) scale(1)',
                      opacity: 1
                    },
                    '50%': {
                      transform: 'translateY(-2px) scale(1.05)',
                      opacity: 0.9
                    }
                  }
                }}
              />

              {/* Indicateur d'enregistrement (petit point rouge animé) */}
              <Box
                sx={{
                  position: 'absolute',
                  top: { xs: 6, md: 8 },
                  right: { xs: 6, md: 8 },
                  width: { xs: 6, md: 8 },
                  height: { xs: 6, md: 8 },
                  borderRadius: '50%',
                  background: '#ff4444',
                  boxShadow: '0 0 8px rgba(255,68,68,0.6)',
                  animation: 'blink 1.5s ease-in-out infinite',
                  '@keyframes blink': {
                    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                    '50%': { opacity: 0.3, transform: 'scale(0.8)' }
                  }
                }}
              />
            </Box>
          </Box>

          {/* Titre avec gradient et taille augmentée */}
          <Typography
            variant="h3"
            component="div"
            sx={{
              fontWeight: 800,
              display: 'flex',
              alignItems: 'center',
              gap: 0.8,
              fontFamily: '"Poppins", "Roboto", sans-serif',
              fontSize: { xs: '1.6rem', md: '2.2rem' },
              letterSpacing: '1px',
              background: 'linear-gradient(135deg, #ffffff 0%, #e3f2fd 50%, #ffffff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textShadow: '0 4px 12px rgba(0,0,0,0.2)',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
            }}
          >
            LINGOO
            <Box
              component="span"
              sx={{
                fontWeight: 500,
                opacity: 0.95,
                fontSize: { xs: '1.3rem', md: '1.8rem' },
                letterSpacing: '0.5px'
              }}
            >
              VOICE
            </Box>
          </Typography>
        </Box>

        {/* Menu hamburger pour mobile (à droite) */}
        <IconButton
          sx={{
            display: { xs: 'flex', md: 'none' },
            color: 'white',
            padding: '12px',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.15)',
              transform: 'scale(1.05)'
            },
            transition: 'all 0.2s'
          }}
          onClick={handleMenuClick}
        >
          <MenuIcon sx={{ fontSize: 32 }} />
        </IconButton>

        {/* Menu de navigation desktop */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, alignItems: 'center' }}>
          {navItems.map((item) => (
            <Button
              key={item.path}
              color="inherit"
              startIcon={item.icon}
              onClick={() => navigate(item.path)}
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: location.pathname === item.path ? 600 : 400,
                backgroundColor: location.pathname === item.path ? 'rgba(255,255,255,0.2)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.15)'
                },
                display: { xs: 'none', md: 'flex' }
              }}
            >
              {item.label}
            </Button>
          ))}

          {/* Bouton déconnexion desktop */}
          {user && (
            <Button
              color="inherit"
              startIcon={<Logout />}
              onClick={() => {
                logout();
                navigate('/login');
              }}
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 400,
                backgroundColor: 'rgba(244, 67, 54, 0.2)',
                color: '#fff',
                ml: 2,
                '&:hover': {
                  backgroundColor: 'rgba(244, 67, 54, 0.3)'
                },
                display: { xs: 'none', md: 'flex' }
              }}
            >
              Déconnexion
            </Button>
          )}
        </Box>
      </Toolbar>

      {/* Menu déroulant mobile */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiPaper-root': {
            mt: 1,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: 2,
            minWidth: 240
          }
        }}
      >
        {navItems.map((item) => (
          <MenuItem
            key={item.path}
            onClick={() => handleMenuItemClick(item.path)}
            sx={{
              py: 2,
              px: 3,
              gap: 2,
              fontSize: '1.1rem',
              backgroundColor: location.pathname === item.path ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(102, 126, 234, 0.15)',
                transform: 'translateX(5px)'
              },
              transition: 'all 0.2s'
            }}
          >
            <ListItemIcon sx={{ minWidth: 50 }}>
              {React.cloneElement(item.icon, { sx: { fontSize: 28, color: '#667eea' } })}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontWeight: location.pathname === item.path ? 600 : 500,
                fontSize: '1.1rem',
                fontFamily: '"Poppins", "Roboto", sans-serif'
              }}
            />
          </MenuItem>
        ))}

        {/* Section utilisateur en bas avec déconnexion */}
        {user && (
          <Box sx={{
            borderTop: '2px solid rgba(0,0,0,0.1)',
            mt: 2,
            pt: 2,
            px: 2,
            pb: 1
          }}>
            {/* Info utilisateur */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 2,
              px: 1
            }}>
              <Box
                component="img"
                src={user.picture}
                alt={user.name}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  border: '2px solid #667eea'
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: '#000',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {user.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#666',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block'
                  }}
                >
                  {user.email}
                </Typography>
              </Box>
            </Box>

            {/* Bouton déconnexion */}
            <MenuItem
              onClick={() => {
                logout();
                navigate('/login');
                handleMenuClose();
              }}
              sx={{
                py: 1.5,
                px: 2,
                gap: 2,
                borderRadius: 2,
                backgroundColor: 'rgba(244, 67, 54, 0.05)',
                color: '#f44336',
                '&:hover': {
                  backgroundColor: 'rgba(244, 67, 54, 0.15)',
                },
                transition: 'all 0.2s'
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Logout sx={{ fontSize: 24, color: '#f44336' }} />
              </ListItemIcon>
              <ListItemText
                primary="Déconnexion"
                primaryTypographyProps={{
                  fontWeight: 500,
                  fontSize: '1rem'
                }}
              />
            </MenuItem>
          </Box>
        )}
      </Menu>
    </AppBar>
  );
});

// Composant interne avec les hooks de navigation
const AppContentWithRouter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuValue, setMobileMenuValue] = React.useState(0);

  // Vérifier si on est sur une page partagée (mode public) ou page d'authentification
  const isSharedPage = location.pathname.startsWith('/shared/') ||
    location.pathname.startsWith('/shared-collection/') ||
    location.pathname.startsWith('/shared-recording/') ||
    location.pathname.startsWith('/translations/shared/') ||
    location.pathname.startsWith('/transcription/shared/');

  const isAuthPage = location.pathname === '/login' ||
    location.pathname.startsWith('/auth/') ||
    location.pathname === '/oauth-callback';

  // Mettre à jour la valeur sélectionnée en fonction du chemin actuel
  React.useEffect(() => {
    const path = location.pathname;
    if (path === '/') setMobileMenuValue(0);
    else if (path === '/voice-recorder') setMobileMenuValue(1);
    else if (path === '/recordings') setMobileMenuValue(1);
    else if (path === '/collections') setMobileMenuValue(2);
    else if (path === '/settings') setMobileMenuValue(3);
  }, [location]);

  const handleBottomNavChange = (event, newValue) => {
    setMobileMenuValue(newValue);
    switch (newValue) {
      case 0:
        navigate('/');
        break;
      case 1:
        navigate('/voice-recorder');
        break;
      case 2:
        navigate('/collections');
        break;
      case 3:
        navigate('/settings');
        break;
      default:
        break;
    }
  };

  // Layout pour les pages partagées (sans navigation)
  if (isSharedPage) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#1a1a2e' }}>
        <Box component="main" sx={{ flex: 1, backgroundColor: '#1a1a2e' }}>
          <ErrorBoundary>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/shared/:id" element={<SharedCollectionPage />} />
                <Route path="/shared-collection/:id" element={<SharedCollectionPage />} />
                <Route path="/shared-recording/:id" element={<SharedRecordingPage />} />
                <Route path="/translations/shared/:shareId" element={<SharedTranslationPage />} />
                <Route path="/transcription/shared/:shareId" element={<SharedTranscriptionPage />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </Box>
      </Box>
    );
  }

  // Layout pour les pages d'authentification (sans navigation)
  if (isAuthPage) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#0f0f23' }}>
        <Box component="main" sx={{ flex: 1 }}>
          <ErrorBoundary>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
                <Route path="/oauth-callback" element={<OAuthCallbackPage />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </Box>
      </Box>
    );
  }

  // Layout normal pour les pages de l'application
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navigation />

      <Box component="main" sx={{ flex: 1, backgroundColor: '#1a1a2e', pb: { xs: 10, md: 0 } }}>
        <ErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/voice-recorder" element={
                <PrivateRoute>
                  <VoiceRecorder />
                </PrivateRoute>
              } />
              <Route path="/my-files" element={
                <PrivateRoute>
                  <MyFilesPage />
                </PrivateRoute>
              } />
              <Route path="/collections" element={
                <PrivateRoute>
                  <VoiceCollectionPageRefactored />
                </PrivateRoute>
              } />
              <Route path="/recordings" element={
                <PrivateRoute>
                  <RecordingsPage />
                </PrivateRoute>
              } />
              <Route path="/settings" element={
                <PrivateRoute>
                  <SettingsPage />
                </PrivateRoute>
              } />
              <Route path="/translations" element={
                <PrivateRoute>
                  <TranslationCollectionPage />
                </PrivateRoute>
              } />
              <Route path="/transcription" element={
                <PrivateRoute>
                  <TranscriptionPage />
                </PrivateRoute>
              } />
              <Route path="/admin" element={
                <PrivateRoute>
                  <AdminRoute>
                    <AdminPage />
                  </AdminRoute>
                </PrivateRoute>
              } />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </Box>

      {/* Menu de navigation inférieur pour mobile */}
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          display: { xs: 'block', md: 'none' },
          zIndex: 1300,
          height: 65,
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
        }}
        elevation={3}
      >
        <BottomNavigation
          value={mobileMenuValue}
          onChange={handleBottomNavChange}
          showLabels
          sx={{
            height: '100%',
            '& .MuiBottomNavigationAction-root': {
              minWidth: 'auto',
              padding: '6px 12px',
              color: 'rgba(0, 0, 0, 0.6)',
              '&.Mui-selected': {
                color: 'primary.main',
                fontSize: '0.75rem'
              }
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.7rem',
              marginTop: '4px',
              '&.Mui-selected': {
                fontSize: '0.75rem'
              }
            }
          }}
        >
          <BottomNavigationAction
            label="Accueil"
            icon={<Home />}
          />
          <BottomNavigationAction
            label="Enregistrer"
            icon={<MicNone />}
          />
          <BottomNavigationAction
            label="Collections"
            icon={<Folder />}
          />
          <BottomNavigationAction
            label="Paramètres"
            icon={<Settings />}
          />
        </BottomNavigation>
      </Paper>

    </Box>
  );
};

// Composant principal de l'application
const AppContent = () => {
  return (
    <Router>
      <AppContentWithRouter />
    </Router>
  );
};

// Composant racine avec tous les providers
const App = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppProvider>
          <AppContent />
        </AppProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;